import {
  useClearRefinements,
  useInstantSearch,
  useMenu,
  useRefinementList,
  useToggleRefinement,
} from "react-instantsearch";
import { useSearchResults } from "@/hooks/use-search-results";
import { ProgramsFiltersFooter } from "./components/ProgramsSearch/components/ProgramsFiltersFooter";
import type { ProgramAvailabilityFilterValue, RecommendedFilterKey } from "./components/ProgramsSearch/constants";
import { ProgramsFilters } from "./components/ProgramsSearch/ProgramsFilters";

// The programs index exposes `availability_mode` as `anytime` | `sessions`; the
// modal presents it as Any / Sessions / Flexible (Flexible == anytime).
const AVAILABILITY_TO_MODE: Record<Exclude<ProgramAvailabilityFilterValue, "any">, string> = {
  sessions: "sessions",
  flexible: "anytime",
};
const MODE_TO_AVAILABILITY: Record<string, ProgramAvailabilityFilterValue> = {
  sessions: "sessions",
  anytime: "flexible",
};

/** Binds the presentational `ProgramsFilters` to InstantSearch facet refinements. */
export function ProgramsFiltersAdapter() {
  const dropOff = useToggleRefinement({ attribute: "drop_off_available", on: true });
  const community = useToggleRefinement({ attribute: "community_program", on: true });
  const familyFavourite = useToggleRefinement({ attribute: "family_favorite", on: true });
  const featured = useToggleRefinement({ attribute: "featured", on: true });
  const availabilityMenu = useMenu({ attribute: "availability_mode" });
  // Read the applied menu refinement from UI state, not `availabilityMenu.items`:
  // a single-select menu drops the refined bucket when 0 hits match it, which would
  // otherwise revert the control to "Any" while the (invisible) refinement sticks.
  const { indexUiState } = useInstantSearch();
  const formatsList = useRefinementList({ attribute: "format", limit: 10 });
  const influencesList = useRefinementList({ attribute: "influences", limit: 20 });
  const languagesList = useRefinementList({ attribute: "instruction_languages", limit: 200 });

  const recommendedRefiners = { community, dropOff, familyFavourite, featured };
  const recommended: Record<RecommendedFilterKey, boolean> = {
    community: community.value.isRefined,
    dropOff: dropOff.value.isRefined,
    familyFavourite: familyFavourite.value.isRefined,
    featured: featured.value.isRefined,
  };
  const onRecommendedChange = (key: RecommendedFilterKey) => {
    const refiner = recommendedRefiners[key];
    refiner.refine(refiner.value);
  };

  const refinedMode = indexUiState.menu?.availability_mode;
  const availability: ProgramAvailabilityFilterValue = refinedMode
    ? (MODE_TO_AVAILABILITY[refinedMode] ?? "any")
    : "any";
  const onAvailabilityChange = (value: ProgramAvailabilityFilterValue) => {
    if (value === "any") {
      if (refinedMode) availabilityMenu.refine(refinedMode);
      return;
    }
    availabilityMenu.refine(AVAILABILITY_TO_MODE[value]);
  };

  const formats = formatsList.items.filter((item) => item.isRefined).map((item) => item.value);
  const influences = influencesList.items.filter((item) => item.isRefined).map((item) => item.value);
  const languages = languagesList.items.filter((item) => item.isRefined).map((item) => item.value);
  const onLanguagesChange = (next: string[]) => {
    const current = new Set(languages);
    const nextSet = new Set(next);
    for (const code of nextSet) if (!current.has(code)) languagesList.refine(code);
    for (const code of current) if (!nextSet.has(code)) languagesList.refine(code);
  };

  return (
    <ProgramsFilters
      availability={availability}
      formats={formats}
      influences={influences}
      languages={languages}
      onAvailabilityChange={onAvailabilityChange}
      onFormatToggle={(value) => formatsList.refine(value)}
      onInfluenceToggle={(value) => influencesList.refine(value)}
      onLanguagesChange={onLanguagesChange}
      onRecommendedChange={onRecommendedChange}
      recommended={recommended}
    />
  );
}

/** The modal footer: Clear all + a "Show N programs" button that dismisses. */
export function ProgramsFiltersFooterAdapter({ onApply }: { onApply: () => void }) {
  const { refine: clearAll } = useClearRefinements();
  const { totalCount } = useSearchResults({ sessionKey: "programs" });

  return <ProgramsFiltersFooter onApply={onApply} onClearAll={clearAll} resultCount={totalCount} />;
}
